import React, { FC } from "react"
import {
  VictoryChart,
  VictoryContainer,
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
  VictoryTooltip,
  VictoryLabel,
} from "victory"
import { colors } from "../../utils/theme"
import { ChartDataPoints } from "../../utils/types"
import ChartTooltip from "../atoms/ChartTooltip"
import {
  TREND_CHART,
  chartAxisLabelStyle,
  chartTickLabelStyle,
} from "./charts/chartTypography"

const chartWrapStyle: React.CSSProperties = {
  marginTop: 16,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
}

const LanguageChart: FC<{
  languageData: ChartDataPoints[][][]
  categories: string[]
}> = ({ languageData, categories }) => (
  <div style={chartWrapStyle}>
    <VictoryChart
      height={TREND_CHART.height}
      width={TREND_CHART.width}
      domainPadding={{ x: [0, 48] }}
      padding={{ top: 18, bottom: 64, left: 70, right: 16 }}
      containerComponent={
        <VictoryContainer
          responsive
          style={{ width: "100%", height: "auto" }}
        />
      }
    >
      <VictoryAxis
        orientation="top"
        tickComponent={<></>}
        tickLabelComponent={<></>}
        style={{
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="right"
        dependentAxis={true}
        tickComponent={<></>}
        tickLabelComponent={<></>}
        style={{
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="bottom"
        tickFormat={(tick, index) => (index % 2 !== 0 ? tick : "")}
        style={{
          tickLabels: chartTickLabelStyle,
          axis: { stroke: colors.neutral.clay },
        }}
      />
      <VictoryAxis
        orientation="left"
        dependentAxis={true}
        tickValues={[0, 0.05, 0.1, 0.15, 0.2, 0.25]}
        label="Proportion of Total Words Typed"
        axisLabelComponent={<VictoryLabel angle={-90} />}
        tickFormat={(tick: number) => {
          switch (tick) {
            case 0:
              return "0.00"
            case 0.05:
              return "0.05"
            case 0.1:
              return "0.10"
            case 0.15:
              return "0.15"
            case 0.2:
              return "0.20"
            case 0.25:
              return "0.25"
            default:
              return ""
          }
        }}
        style={{
          tickLabels: chartTickLabelStyle,
          axisLabel: chartAxisLabelStyle,
          grid: { stroke: colors.neutral.clay },
          axis: { stroke: colors.neutral.clay },
        }}
      />

      {languageData.map((words) =>
        words.map((data, index) => (
          <VictoryLine
            key={index}
            categories={{
              x: categories,
            }}
            style={{
              data: {
                stroke: data[0]?.darkColor,
                strokeWidth: 2,
              },
            }}
            data={data.map(({ x, y }) => ({ x, y }))}
          />
        ))
      )}

      {languageData.map((words) =>
        words.map((data, index) => (
          <VictoryScatter
            key={index}
            categories={{
              x: categories,
            }}
            data={data}
            symbol="circle"
            size={6}
            style={{
              data: {
                stroke: data[0]?.darkColor,
                strokeWidth: 2,
                fill: data[0]?.lightColor,
              },
            }}
            labelComponent={
              <VictoryTooltip
                flyoutComponent={
                  <ChartTooltip
                    width={184}
                    otherData={languageData}
                    otherDataRange={0.5}
                    crosshair={{
                      chartHeight: 265,
                      paddingTop: 18,
                      paddingBottom: 64,
                    }}
                  />
                }
              />
            }
          />
        ))
      )}
    </VictoryChart>

    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "8px 24px",
        marginTop: 8,
        width: "100%",
        minWidth: 0,
      }}
    >
      {[
        {
          name: "Positive",
          fill: colors.trendingData.orange.dark,
        },
        {
          name: "Negative",
          fill: colors.trendingData.blue.dark,
        },
        {
          name: "Self-Focus",
          fill: colors.trendingData.red.dark,
        },
        {
          name: "Rigid",
          fill: colors.trendingData.green.dark,
        },
      ].map((item) => (
        <div
          key={item.name}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: item.fill,
              flexShrink: 0,
            }}
          />
          <span
            style={{
              fontFamily: "Lato",
              color: colors.neutral.asphalt,
              fontSize: 14,
              fontWeight: 400,
            }}
          >
            {item.name}
          </span>
        </div>
      ))}
    </div>
  </div>
)

export default LanguageChart
